import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

type SeoConfig = {
  title: string;
  description: string;
  url: string;
  image: string;
  schemaType?: string;
};

const siteUrl = 'https://learnaihub.net';
const logoUrl = `${siteUrl}/logo.png`;

const seoByPath: Record<string, SeoConfig> = {
  '/': {
    title: 'Learn AI Skills to Scale, Automate & Grow Business',
    description: 'Learn AI fundamentals, business automation, market analysis, and ChatGPT branding with practical, ROI-focused courses.',
    url: `${siteUrl}/`,
    image: 'https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?q=80&w=800&auto=format&fit=crop',
    schemaType: 'WebSite',
  },
  '/pricing': {
    title: 'Affordable Pricing Plans for AI Learning and Business Growth',
    description: 'Explore LearnAIHub pricing plans and get access to AI courses, business automation training, ChatGPT strategies, and expert-led learning.',
    url: `${siteUrl}/pricing`,
    image: logoUrl,
  },
  '/unsubscribe': {
    title: 'Cancel Subscription Plan | LearnAI Hub',
    description: 'Request cancellation of your LearnAI Hub subscription plan or manage your active plan preferences.',
    url: `${siteUrl}/unsubscribe`,
    image: logoUrl,
  },
  '/terms-and-conditions': {
    title: 'Terms and Conditions | LearnAI Hub',
    description: 'Read the Terms and Conditions for LearnAI Hub. Learn about your rights, responsibilities, and the rules governing use of our website and services.',
    url: `${siteUrl}/terms-and-conditions`,
    image: logoUrl,
  },
  '/privacy-policy': {
    title: 'Privacy Policy | LearnAI Hub',
    description: 'Review the Privacy Policy for LearnAI Hub to understand how we collect, use, and protect your personal information when you use our website and services.',
    url: `${siteUrl}/privacy-policy`,
    image: logoUrl,
  },
  '/register': {
    title: 'LearnAI Hub Signup | Join & Start Learning AI Today',
    description: 'Sign up for LearnAI Hub to access expert-led AI courses, automation strategies, and business growth training designed for professionals.',
    url: `${siteUrl}/register`,
    image: logoUrl,
  },
  '/signup': {
    title: 'LearnAI Hub Signup | Join & Start Learning AI Today',
    description: 'Sign up for LearnAI Hub to access expert-led AI courses, automation strategies, and business growth training designed for professionals.',
    url: `${siteUrl}/register`,
    image: logoUrl,
  },
  '/imprint': {
    title: 'Imprint & Legal Information | LearnAI Hub',
    description: "Access LearnAI Hub's company registration details, contact information, legal disclosures, and website ownership information.",
    url: `${siteUrl}/imprint`,
    image: logoUrl,
  },
};

const fallbackSeo: SeoConfig = seoByPath['/'];

const setMetaTag = (selector: string, attribute: 'name' | 'property', value: string, content: string) => {
  let element = document.head.querySelector<HTMLMetaElement>(selector);

  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, value);
    document.head.appendChild(element);
  }

  element.setAttribute('content', content);
};

const setCanonical = (href: string) => {
  let element = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');

  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', 'canonical');
    document.head.appendChild(element);
  }

  element.setAttribute('href', href);
};

const buildSchema = (seo: SeoConfig) => {
  const publisher = {
    '@type': 'Organization',
    name: 'LearnAI Hub',
    url: siteUrl,
    logo: logoUrl,
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'support@learnaihub.net',
      contactType: 'customer support',
    },
  };

  return {
    '@context': 'https://schema.org',
    '@type': seo.schemaType || 'WebPage',
    name: seo.title,
    headline: seo.title,
    description: seo.description,
    url: seo.url,
    image: seo.image,
    publisher,
  };
};

const setSchema = (seo: SeoConfig) => {
  const id = 'route-schema';
  let element = document.getElementById(id) as HTMLScriptElement | null;

  if (!element) {
    element = document.createElement('script');
    element.id = id;
    element.type = 'application/ld+json';
    document.head.appendChild(element);
  }

  element.textContent = JSON.stringify(buildSchema(seo));
};

const Seo = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const seo = seoByPath[pathname] || fallbackSeo;

    document.title = seo.title;
    setMetaTag('meta[name="description"]', 'name', 'description', seo.description);
    setMetaTag('meta[property="og:title"]', 'property', 'og:title', seo.title);
    setMetaTag('meta[property="og:description"]', 'property', 'og:description', seo.description);
    setMetaTag('meta[property="og:url"]', 'property', 'og:url', seo.url);
    setMetaTag('meta[property="og:type"]', 'property', 'og:type', 'website');
    setMetaTag('meta[property="og:image"]', 'property', 'og:image', seo.image);
    setMetaTag('meta[name="twitter:title"]', 'name', 'twitter:title', seo.title);
    setMetaTag('meta[name="twitter:description"]', 'name', 'twitter:description', seo.description);
    setMetaTag('meta[name="twitter:image"]', 'name', 'twitter:image', seo.image);
    setCanonical(seo.url);
    setSchema(seo);
  }, [pathname]);

  return null;
};

export default Seo;
